/* eslint-disable no-nested-ternary */
import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Up from '@material-ui/icons/TrendingUp';
import Down from '@material-ui/icons/TrendingDown';
import Flat from '@material-ui/icons/TrendingFlat';
import ReportScatterChart from './ReportScatterChart';
import ReportBarChart from './ReportBarChart';
import ReportTable from './ReportTable';
import { COLORS, isObjectsEqual, numberWithCommas } from '../../../../utils';

const styles = ({
  card: {
    textAlign: "center",
    backgroundColor: "#E6EBEF"
  },
  container: {
    margin: "10px"
  },
  leftIcon: {
    marginRight: 2
  },
  title: {
    fontSize: 18,
  },
})

function getDetailedReports(reports, mode, coinsForEachServerPerMinute) {
  if (mode === 'min') { return reports; }

  const detailedReports = [];
  for (let i = 0; i < reports.length; i += 1) {
    const row = {};
    const rowKeys = Object.keys(reports[i]);
    for (let j = 0; j < rowKeys.length; j += 1) {
      row[rowKeys[j]] = rowKeys[j] === 'date' ? reports[i][rowKeys[j]] : reports[i][rowKeys[j]] * coinsForEachServerPerMinute[rowKeys[j]];
    }
    detailedReports.push(row);
  }
  return detailedReports;
}

function correctInterval(reports, interval, cores) {
  if (interval === 'daily') return reports;
  const newReports = [];
  const dates = [];
  for (let i = 0; i < reports.length; i += 1) {
    const date = new Date(reports[i].date);
    const day = date.getDay();
    const newDate = interval === 'monthly' ? date.toLocaleString('default', { month: 'short', year: "numeric" }) :
      new Date(date.getFullYear(), date.getMonth(), date.getDate() + (day === 0 ? -6 : 1) - day).toLocaleString('default', { month: 'short', day: 'numeric', year: "numeric" });
    if (dates.includes(newDate)) {
      for (let j = 0; j < newReports.length; j += 1) {
        if (newReports[j].date === newDate) {
          for (let k = 0; k < cores.length; k += 1) {
            const amount = reports[i][cores[k]];
            newReports[j][cores[k]] += amount || 0;
          }
        }
      }
    } else {
      const row = { ...reports[i] };
      row.date = newDate;
      dates.push(newDate);
      newReports.push(row);
    }
  }
  return newReports;
}

function addTotalColumn(reports) {
  for (let i = 0; i < reports.length; i += 1) {
    reports[i].Total = 0;

    Object.keys(reports[i]).map(key => {
      reports[i].Total += (key === "date" || key === "Total" ? 0 : reports[i][key]);
      return 0;
    });

  }
  return reports;
}

function getTotalReports(detailedReports, mode, cores) {
  return cores.map((core) => (
    {
      "core": core,
      [mode]: detailedReports.reduce((cnt, report) => { return cnt + (report[core] ? report[core] : 0) }, 0)
    }
  ))
}

function getTotalSum(totalReports, mode) {
  let total = 0;
  for (let i = 0; i < totalReports.length; i += 1) {
    total += totalReports[i][mode];
  }
  return total;
}

function getReportsForTable(usersData) {
  if (!usersData) { return null; }
  const reportsForTable = []
  for (let i = 0; i < usersData.length; i += 1) {
    const row = {
      "balance": usersData[i].balance,
      "name": usersData[i].username,
      "cores_info": usersData[i].cores.map((core) => (
        {
          "cores": core,
          "sum": usersData[i].data.reduce((cnt, report) => { return cnt + report[core] }, 0)
        }
      ))
    }
    reportsForTable.push(row);
  }
  return reportsForTable;
}

function getColorsForServers(cores) {
  const result = {};
  for (const core in cores) {
    result[cores[core]] = COLORS[(core - 1) % 9 + 1];
  }
  result.Total = "#027E00";
  return result;
}

class ShowReports extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: "min",
      interval: "daily"
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { reports, is_plugin } = this.props;
    const { mode, interval } = this.state;
    const isReportChanged = !isObjectsEqual(reports, nextProps.reports);
    if (nextProps.is_plugin !== is_plugin || nextState.mode !== mode || nextState.interval !== interval || isReportChanged) {
      return true;
    }
    return false;
  }

  changeInterval = (interval) => {
    this.setState({ interval });
  }

  render() {
    const { classes, is_plugin, coinsForEachServerPerMinute, reports, usersData, getUserReport, userBalance, briefReports, isSuperuser, coreNames } = this.props;
    const cores = Object.keys(coinsForEachServerPerMinute);
    const coresForScatterChart = [...cores];
    coresForScatterChart.push("Total");
    const { mode, interval } = this.state;

    let detailedReports = getDetailedReports(reports, mode, coinsForEachServerPerMinute);
    detailedReports = correctInterval(detailedReports, interval, cores);
    detailedReports = addTotalColumn(detailedReports);
    const totalReports = getTotalReports(detailedReports, mode, cores);
    const total = getTotalSum(totalReports, mode);
    const reportsForTable = getReportsForTable(usersData);
    const colors = getColorsForServers(coreNames);

    return (
      <Grid justify="center" className={classes.container}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" gutterBottom align="right">
                The server names indicate the speed that Stockfish 17 normally reaches on them.
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <div style={{ textAlign: "right" }}>
                Show in: &nbsp;
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mode === 'min'}
                      onChange={() => { this.setState({ mode: 'min' }) }}
                      value="hourly"
                      color="primary"
                    />
                  }
                  label="Time"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mode === 'coins'}
                      onChange={() => { this.setState({ mode: 'coins' }) }}
                      value="total"
                      color="primary"
                    />
                  }
                  label="Coin"
                />
              </div>
              {isSuperuser && <div style={{ textAlign: "right" }}>
                Usage: &nbsp;
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={is_plugin === null}
                      onChange={() => { this.props.usageChange(null) }}
                      value="hourly"
                      color="primary"
                    />
                  }
                  label="All"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={is_plugin === false}
                      onChange={() => { this.props.usageChange(false) }}
                      value="total"
                      color="primary"
                    />
                  }
                  label="Web Pro"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={is_plugin === true}
                      onChange={() => { this.props.usageChange(true) }}
                      value="total"
                      color="primary"
                    />
                  }
                  label="Plugin"
                />
              </div>}
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1} direction="column" alignItems="left">
                <Grid item>
                  <div>
                    <Button size="small" color="primary" variant={interval === 'daily' ? "contained" : "outlined"} onClick={(e) => this.changeInterval("daily")}>Daily</Button>
                    <Button size="small" color="primary" variant={interval === 'weekly' ? "contained" : "outlined"} onClick={(e) => this.changeInterval("weekly")}>Weekly</Button>
                    <Button size="small" color="primary" variant={interval === 'monthly' ? "contained" : "outlined"} onClick={(e) => this.changeInterval("monthly")}>Monthly</Button>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ReportScatterChart reports={detailedReports} cores={coresForScatterChart} mode={mode} colors={colors} />
        </Grid>

        <Grid item xs={12}>
          <ReportBarChart reports={totalReports} mode={mode} colors={colors} />
        </Grid>

        {
          usersData &&
          <Grid item xs={12}>
            <ReportTable reports={reportsForTable} cores={cores} getUserReport={getUserReport} mode={mode} coinsForEachServerPerMinute={coinsForEachServerPerMinute} numberWithCommas={numberWithCommas} />
          </Grid>
        }

        <Grid item xs={12} style={{ marginTop: '20px' }}>
          <Card className={classes.card}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h5" component="h2">
                    Total used {mode === 'min' ? 'minutes' : mode}: {numberWithCommas(total)}
                  </Typography>
                  <Typography variant="h5" component="h2">
                    {userBalance && `Balance: ${userBalance} coins`}
                  </Typography>
                </Grid>
                {
                  isSuperuser &&
                  <Grid item xs={12} style={{ textAlign: "-webkit-center" }}>
                    <Typography variant="caption" className={classes.title} color="textSecondary" gutterBottom>
                      Training Statistics:
                    </Typography>
                    <List style={{ padding: 0, maxWidth: "320px", textAlign: "center" }}>
                      {
                        briefReports.map((info) =>
                          <ListItem key={info.number_of_days} style={{ paddingBottom: 0 }}>
                            <ListItemText style={{ color: "grey" }} primary={info.number_of_days === 1 ? " Today" : ` Last ${info.number_of_days} days`} />
                            <ListItemText style={{ color: "black", fontWeight: "bold" }} primary={`${info.minutes} min.`} />
                            <Button style={{ color: (info.diff === 0 ? "grey" : info.increase ? "green" : "red"), cursor: 'text', backgroundColor: 'transparent' }} disableRipple>
                              {
                                info.diff === 0 ? <Flat className={classes.leftIcon} /> :
                                  info.increase ? <Up className={classes.leftIcon} /> :
                                    <Down className={classes.leftIcon} />
                              }
                              {`${info.diff}%`}
                            </Button>
                          </ListItem>
                        )
                      }
                    </List>
                  </Grid>
                }
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }
}

export default withStyles(styles)(ShowReports);
